import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Info, IndianRupee, Layers, Tag } from 'lucide-react';
import MultiImageUploader from '@/components/admin/MultiImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const warrantyOptions = ['1 Year Warranty', '2 Year Warranty', 'No Warranty', 'Not Applicable'];

const newAttrId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `attr_${Math.random().toString(36).slice(2)}`;

const dictToAttrArray = (dict) =>
  Object.entries(dict || {}).map(([key, value]) => ({ id: newAttrId(), key, value }));

const attrArrayToDict = (arr) => {
  const dict = {};
  for (const row of arr || []) {
    const k = (row.key || '').trim();
    const v = (row.value || '').trim();
    if (k && v) dict[k] = v;
  }
  return dict;
};

const emptyForm = {
  name: '',
  description: '',
  price: '',
  original_price: '',
  shipping_fee: '0',
  category: 'mobiles',
  brand: '',
  image_url: '',
  images: [],
  stock: 100,
  barcode: '',
  warranty: 'Not Applicable',
  group_name: '',
  variant_attributes: [],
  is_pre_order: false
};

const SectionCard = ({ icon: Icon, title, children, className = '' }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-4 sm:p-5 ${className}`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-[#0047AB]" />
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title}</h3>
    </div>
    {children}
  </div>
);

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const ProductFormDialog = ({
  open,
  onOpenChange,
  editingProduct,
  brands,
  categories,
  existingGroups,
  existingAttributes,
  onSaved
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [groupSuggestionsOpen, setGroupSuggestionsOpen] = useState(false);
  const [openAttrKeyDropdown, setOpenAttrKeyDropdown] = useState(null);
  const [openAttrValDropdown, setOpenAttrValDropdown] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editingProduct) {
      setFormData({
        name: editingProduct.name,
        description: editingProduct.description,
        price: editingProduct.price.toString(),
        original_price: editingProduct.original_price ? editingProduct.original_price.toString() : '',
        shipping_fee: editingProduct.shipping_fee ? editingProduct.shipping_fee.toString() : '0',
        category: editingProduct.category,
        brand: editingProduct.brand || '',
        image_url: editingProduct.image_url,
        images: editingProduct.images || [],
        stock: editingProduct.stock,
        barcode: editingProduct.barcode || '',
        warranty: editingProduct.warranty || 'Not Applicable',
        group_name: editingProduct.group_name || '',
        variant_attributes: dictToAttrArray(editingProduct.variant_attributes),
        is_pre_order: !!editingProduct.is_pre_order
      });
    } else {
      setFormData(emptyForm);
    }
    setGroupSuggestionsOpen(false);
    setOpenAttrKeyDropdown(null);
    setOpenAttrValDropdown(null);
  }, [open, editingProduct]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        shipping_fee: parseFloat(formData.shipping_fee) || 0,
        stock: parseInt(formData.stock),
        images: formData.images || [],
        group_name: (formData.group_name || '').trim() || null,
        variant_attributes: attrArrayToDict(formData.variant_attributes),
        is_pre_order: !!formData.is_pre_order
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, getAuthHeaders());
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, productData, getAuthHeaders());
        toast.success('Product created successfully');
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const updateAttrRow = (rowIdx, patch) => {
    setFormData((prev) => {
      const next = [...prev.variant_attributes];
      next[rowIdx] = { ...next[rowIdx], ...patch };
      return { ...prev, variant_attributes: next };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 shrink-0 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-xl">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          {editingProduct?.product_code && (
            <span
              className="mr-8 inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-md px-3 py-1 text-sm"
              data-testid="edit-product-code-display"
            >
              <span className="text-[11px] uppercase tracking-wide text-slate-500">Product ID</span>
              <span className="font-mono font-semibold text-[#0047AB]">{editingProduct.product_code}</span>
            </span>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto bg-slate-50 px-4 sm:px-6 py-5">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
              {/* LEFT COLUMN */}
              <div className="lg:col-span-3 space-y-5">
                <SectionCard icon={Info} title="Basic Information">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Product Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        autoFocus
                        className="mt-1.5"
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        required
                        className="mt-1.5"
                        data-testid="product-description-input"
                      />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard icon={IndianRupee} title="Pricing & Inventory">
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="original_price">Original Price (₹)</Label>
                      <Input
                        id="original_price"
                        name="original_price"
                        type="number"
                        value={formData.original_price}
                        onChange={handleChange}
                        placeholder="MRP"
                        className="mt-1.5"
                        data-testid="product-original-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="price">Offer Price (₹) *</Label>
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        value={formData.price}
                        onChange={handleChange}
                        required
                        className="mt-1.5"
                        data-testid="product-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stock">Stock *</Label>
                      <Input
                        id="stock"
                        name="stock"
                        type="number"
                        value={formData.stock}
                        onChange={handleChange}
                        required
                        className="mt-1.5"
                        data-testid="product-stock-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping_fee">Shipping (₹)</Label>
                      <Input
                        id="shipping_fee"
                        name="shipping_fee"
                        type="number"
                        min="0"
                        value={formData.shipping_fee}
                        onChange={handleChange}
                        placeholder="0 = Free"
                        className="mt-1.5"
                        data-testid="product-shipping-fee-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Shipping fee of 0 means free shipping.</p>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={!!formData.is_pre_order}
                        onChange={(e) => setFormData((prev) => ({ ...prev, is_pre_order: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 accent-[#0047AB]"
                        data-testid="product-pre-order-checkbox"
                      />
                      <span className="flex-1">
                        <span className="block text-sm font-semibold text-slate-900">Mark as Pre-Order</span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Hides the price on storefront and replaces &ldquo;Add to Cart&rdquo; with a
                          &ldquo;Pre-Order&rdquo; button. Customers submit orders without payment;
                          you get an email to follow up when stock is available.
                        </span>
                      </span>
                    </label>
                  </div>
                </SectionCard>

                <SectionCard icon={Tag} title="Classification">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value, brand: '' }))}
                        className={`${selectClass} mt-1.5`}
                        data-testid="product-category-select"
                      >
                        {categories.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="brand">Brand (Optional)</Label>
                      <select
                        id="brand"
                        name="brand"
                        value={formData.brand || ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, brand: e.target.value }))}
                        className={`${selectClass} mt-1.5`}
                        data-testid="product-brand-select"
                      >
                        <option value="">No Brand</option>
                        {brands[formData.category] && brands[formData.category].length > 0 ? (
                          brands[formData.category].map((brand) => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))
                        ) : (
                          <option disabled>Loading brands...</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="barcode">Barcode</Label>
                      <Input
                        id="barcode"
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        placeholder="e.g., 8901234567890"
                        className="mt-1.5"
                        data-testid="product-barcode-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="warranty">Warranty</Label>
                      <select
                        id="warranty"
                        name="warranty"
                        value={formData.warranty}
                        onChange={(e) => setFormData((prev) => ({ ...prev, warranty: e.target.value }))}
                        className={`${selectClass} mt-1.5`}
                        data-testid="product-warranty-select"
                      >
                        {warrantyOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* RIGHT COLUMN */}
              <div className="lg:col-span-2 space-y-5">
                <SectionCard icon={Layers} title="Product Images">
                  <MultiImageUploader
                    value={formData.images}
                    mainImage={formData.image_url}
                    onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
                    onMainImageChange={(url) => setFormData((prev) => ({ ...prev, image_url: url }))}
                  />
                </SectionCard>

                <SectionCard icon={Layers} title="Variant Group (Optional)">
                  <div className="relative">
                    <Label htmlFor="group_name">Group Name</Label>
                    <Input
                      id="group_name"
                      name="group_name"
                      value={formData.group_name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => ({ ...prev, group_name: v }));
                        setGroupSuggestionsOpen(true);
                      }}
                      onFocus={() => setGroupSuggestionsOpen(true)}
                      onBlur={() => setTimeout(() => setGroupSuggestionsOpen(false), 200)}
                      placeholder="e.g. iPhone 17 Pro"
                      className="mt-1.5"
                      data-testid="product-group-name-input"
                      autoComplete="off"
                    />
                    {groupSuggestionsOpen && existingGroups.length > 0 && (
                      <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="group-suggestions-dropdown">
                        {existingGroups
                          .filter((g) =>
                            !formData.group_name.trim() ||
                            g.name.toLowerCase().includes(formData.group_name.trim().toLowerCase())
                          )
                          .slice(0, 8)
                          .map((g) => (
                            <button
                              key={g.name}
                              type="button"
                              tabIndex={-1}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, group_name: g.name }));
                                setGroupSuggestionsOpen(false);
                              }}
                              className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-slate-50"
                              data-testid={`group-suggestion-${g.name}`}
                            >
                              <span className="text-slate-900">{g.name}</span>
                              <span className="text-xs text-slate-500">{g.variant_count} variant{g.variant_count === 1 ? '' : 's'}</span>
                            </button>
                          ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1.5">
                      Products with the same group name are merged as variants on the storefront. Leave blank for a standalone product.
                    </p>
                  </div>

                  {formData.group_name.trim() && (
                    <div className="mt-4">
                      <Label>Variant Attributes</Label>
                      <p className="text-xs text-slate-500 mb-2">
                        Key-value pairs that differentiate this variant (e.g. color: Blue, storage: 256GB).
                      </p>
                      <div className="space-y-2">
                        {formData.variant_attributes.map((row, rowIdx) => {
                          const matchingAttr = existingAttributes.find(
                            (a) => a.key.toLowerCase() === (row.key || '').toLowerCase()
                          );
                          const knownValues = matchingAttr?.values || [];
                          const keySuggestions = existingAttributes
                            .filter((a) => !row.key.trim() || a.key.toLowerCase().includes(row.key.trim().toLowerCase()))
                            .filter((a) => a.key.toLowerCase() !== (row.key || '').toLowerCase())
                            .slice(0, 6);
                          const valSuggestions = knownValues
                            .filter((v) => !row.value.trim() || v.toLowerCase().includes(row.value.trim().toLowerCase()))
                            .filter((v) => v.toLowerCase() !== (row.value || '').toLowerCase())
                            .slice(0, 8);
                          return (
                            <div key={row.id} className="flex gap-2" data-testid={`variant-attr-row-${rowIdx}`}>
                              <div className="relative flex-1">
                                <Input
                                  value={row.key}
                                  onChange={(e) => updateAttrRow(rowIdx, { key: e.target.value })}
                                  onFocus={() => setOpenAttrKeyDropdown(row.id)}
                                  onBlur={() => setTimeout(() => setOpenAttrKeyDropdown((cur) => (cur === row.id ? null : cur)), 150)}
                                  placeholder="Attribute (e.g. color)"
                                  autoComplete="off"
                                  data-testid={`variant-attr-key-${rowIdx}`}
                                />
                                {openAttrKeyDropdown === row.id && keySuggestions.length > 0 && (
                                  <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                    {keySuggestions.map((a) => (
                                      <button
                                        key={a.key}
                                        type="button"
                                        tabIndex={-1}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                          updateAttrRow(rowIdx, { key: a.key });
                                          setOpenAttrKeyDropdown(null);
                                        }}
                                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-slate-50"
                                        data-testid={`attr-key-suggestion-${a.key}`}
                                      >
                                        <span className="text-slate-900">{a.key}</span>
                                        <span className="text-xs text-slate-500">{a.usage_count} use{a.usage_count === 1 ? '' : 's'}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="relative flex-1">
                                <Input
                                  value={row.value}
                                  onChange={(e) => updateAttrRow(rowIdx, { value: e.target.value })}
                                  onFocus={() => setOpenAttrValDropdown(row.id)}
                                  onBlur={() => setTimeout(() => setOpenAttrValDropdown((cur) => (cur === row.id ? null : cur)), 150)}
                                  placeholder="Value (e.g. Blue)"
                                  autoComplete="off"
                                  data-testid={`variant-attr-value-${rowIdx}`}
                                />
                                {openAttrValDropdown === row.id && valSuggestions.length > 0 && (
                                  <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                    {valSuggestions.map((v) => (
                                      <button
                                        key={v}
                                        type="button"
                                        tabIndex={-1}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                          updateAttrRow(rowIdx, { value: v });
                                          setOpenAttrValDropdown(null);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-900"
                                        data-testid={`attr-value-suggestion-${v}`}
                                      >
                                        {v}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                tabIndex={-1}
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    variant_attributes: prev.variant_attributes.filter((_, i) => i !== rowIdx)
                                  }));
                                }}
                                data-testid={`remove-variant-attr-${rowIdx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              variant_attributes: [...prev.variant_attributes, { id: newAttrId(), key: '', value: '' }]
                            }));
                          }}
                          data-testid="add-variant-attr-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Attribute
                        </Button>
                      </div>
                    </div>
                  )}
                </SectionCard>
              </div>
            </div>
          </div>

          {/* STICKY FOOTER */}
          <div className="shrink-0 px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-product-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#0047AB] hover:bg-[#003380] min-w-[160px]"
              data-testid="save-product-btn"
            >
              {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
